import { useMemo, useState, ReactNode } from "react";
import {
  filterAndSortLessons,
  LessonFilters,
  createFilterUpdateHandler,
  FilterUpdate,
} from "@/utils/filter";
import { Lesson } from "@/types/tutor";
import LoadingSpinner from "./LoadingSpinner";

interface FilteredLessonsViewProps<T extends Lesson> {
  lessons: T[];
  children: (filteredLessons: T[]) => ReactNode;
  emptyState?: ReactNode;
  loading?: boolean;
  initialFilterType?: string;
  renderFilters?: (
    onFiltersChange: (update: FilterUpdate) => void,
    filteredLessons: T[],
  ) => ReactNode;
}

const FilteredLessonsView = <T extends Lesson>({
  lessons,
  children,
  emptyState,
  loading,
  initialFilterType = "lesson_name",
  renderFilters,
}: FilteredLessonsViewProps<T>) => {
  const [filters, setFilters] = useState<LessonFilters>({
    filterType: initialFilterType,
    sortOrder: "asc",
    searchValue: "",
    statusFilter: "all",
  });

  const filteredLessons = useMemo(
    () => filterAndSortLessons(lessons, filters),
    [lessons, filters],
  );

  const handleFiltersChange = createFilterUpdateHandler(setFilters);

  return (
    <>
      {renderFilters && renderFilters(handleFiltersChange, filteredLessons)}
      {loading ? (
        <LoadingSpinner message="Loading lessons..." />
      ) : filteredLessons.length === 0 && lessons.length === 0 ? (
        emptyState || null
      ) : filteredLessons.length === 0 ? (
        <div className="et-empty" role="status">
          <h3>No lessons found</h3>
          <p>Try another search or filter.</p>
        </div>
      ) : (
        children(filteredLessons)
      )}
    </>
  );
};

export default FilteredLessonsView;
